import {
  AjaxStatusCore,
  GraphQLCore,
  MessagePopupCore,
  objectAtPath,
} from '@utahdts/dts-react-common';
import webserviceContext from './webserviceContext';
import appStore from '../mobx/appStore';
import errorCodeMessages from '../roles/errorCodeMessages';
import { getCachedToken } from '../auth/tokenStore';
import { debugLog } from '../debug/devLogger';
import { trackApiOperation, graphQLOperationName } from '../analytics/analytics';

export const ajaxStatus = new AjaxStatusCore(
  (id, isStarting) =>
    (appStore.isAjaxing = (appStore.isAjaxing || 0) + (isStarting ? 1 : -1)),
);

const graphQLClient = new GraphQLCore({
  graphQLUrl: webserviceContext,
  ajaxStatusCore: ajaxStatus,
  loadDefaultsCallback: (defaults) => {
    // Attach the Firebase ID token as a Bearer credential. Prefer the
    // synchronously-readable cache kept fresh by FirebaseAuthProvider
    // (onAuthStateChanged + 50-minute refresh); fall back to the token mirrored
    // onto appStore.oidcUser (e.g. rehydrated from local storage on reload).
    const token = getCachedToken() || appStore.oidcUser?.access_token;
    if (token) {
      defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  },
  rawPromiseCallback: (promise) =>
    promise.then((data) => {
      const errors = objectAtPath(data, 'data.errors');
      if (errors) {
        const raw = errors[0].message || '';
        const [code, msg] = raw.includes('::') ? raw.split('::', 2) : [null, raw];
        if (code === 'RATE_LIMITED') {
          window.rateLimitUntil = Date.now() + 60000;
        }
        if (code && errorCodeMessages[code]) {
          const mapped = errorCodeMessages[code];
          MessagePopupCore.addMessage({
            title: mapped.title,
            message: mapped.message,
            subMessage: msg,
          });
        } else if (raw === 'Internal server error') {
          MessagePopupCore.addMessage({
            title: 'Signed Out',
            message:
              'You have been signed out for inactivity. Please sign in and try again.',
          });
        } else {
          MessagePopupCore.addMessage({
            title: 'Webservice Communication Interruption',
            message: 'There was an error communicating with the webservice.',
            subMessage: raw,
          });
        }
        throw raw;
      }
      return data;
    }).catch((err) => {
      const message = err?.message || String(err);
      if (/NetworkError|Failed to fetch|network/i.test(message)) {
        // retry once
        return promise.then((data2) => data2).catch((error_) => {
          MessagePopupCore.addMessage({
            title: 'Network Issue',
            message: 'Unable to reach the server. Please check your connection and try again.',
            subMessage: error_?.message || String(error_),
          });
          throw error_;
        });
      }
      throw err;
    }),
});

// Debug instrumentation: when the devLogger is enabled (server DEBUG=true), log
// every GraphQL operation as it is entered and again with its resolved data or
// error. No-op otherwise. Guarded so a mocked GraphQLCore lacking query/mutation
// (e.g. in unit tests) is left untouched.
const instrument = (methodName) => {
  const original = graphQLClient[methodName];
  if (typeof original !== 'function') {
    return;
  }
  const bound = original.bind(graphQLClient);
  graphQLClient[methodName] = (graphQL, ...rest) => {
    debugLog('api', `${methodName} \u2192 entered`, graphQL);
    // Analytics: measure every GraphQL/Cloud-Function invocation (operation,
    // query|mutation, success|error, duration). Best-effort and never throws.
    const operation = graphQLOperationName(graphQL);
    const startedAt = Date.now();
    let result;
    try {
      result = bound(graphQL, ...rest);
    } catch (error) {
      debugLog('api', `${methodName} \u2717 threw`, error);
      trackApiOperation({
        operation,
        type: methodName,
        status: 'error',
        durationMs: Date.now() - startedAt,
      });
      throw error;
    }
    if (result && typeof result.then === 'function') {
      return result.then(
        (data) => {
          debugLog('api', `${methodName} \u2190 returned`, data);
          trackApiOperation({
            operation,
            type: methodName,
            status: 'success',
            durationMs: Date.now() - startedAt,
          });
          return data;
        },
        (error) => {
          debugLog('api', `${methodName} \u2717 rejected`, error);
          trackApiOperation({
            operation,
            type: methodName,
            status: 'error',
            durationMs: Date.now() - startedAt,
          });
          throw error;
        },
      );
    }
    // Synchronous (non-promise) result — still record the invocation.
    trackApiOperation({
      operation,
      type: methodName,
      status: 'success',
      durationMs: Date.now() - startedAt,
    });
    return result;
  };
};
instrument('query');
instrument('mutation');

export default graphQLClient;
